<script setup>
import { ref, onMounted, watch } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import Multiselect from "@vueform/multiselect";
import ScopeItemRow from "../../components/ScopeItemRow.vue";
import ScopeRichTextEditor from "../../components/ScopeRichTextEditor.vue";
import {
  scopeCodeOptions,
  scopeItemsDefault,
  scopeItemUnitTypes,
  scopeSections,
  toCurrency,
} from "../../assets/js/helpers";
import LoadingSpinner from "../../components/LoadingSpinner.vue";
import setTooltip from "@/assets/js/tooltip.js";
import PDFVuer from "./PDFVuer.vue";

// Props
const props = defineProps({
  propertyId: {
    type: String,
    required: true,
  },
  propertyMetadata: {
    type: Object,
  },
  contractId: {
    type: String,
    required: true,
  },
  address: {
    type: String,
    required: true,
  },
  loadContract: {
    type: Function,
  },
  contractStatus: {
    type: Number,
  },
  scopeFileUrl: {
    type: String,
  },
  setActions: {
    type: Function,
  },
});

// Reactive references
const loading = ref(false);
const scopeObj = ref({});
const unsavedChanges = ref(false);
const resetModal = ref({ open: false, type: null });
const showPdf = ref(false);
const currentSection = ref("");

const section2Subitems = ref([]);
const scopeTradeOptions = ref([]);

// Vuex store and toast notifications
const store = useStore();
const toast = useToast();

// API endpoint and authorization headers
const apiEndpoint = store.state.apiEndpoint;
const token = sessionStorage.getItem("token");
const headers = {
  headers: {
    Authorization: `Bearer ${token}`,
  },
};

// Disable editing if contract status is not 1
const disabledEdit = props.contractStatus !== 1;

// Default item structure
const defaultItem = {
  item: "",
  item_html: "",
  qty: "",
  unit: "",
  cost: "",
  trade_costs: {},
  item_code: "",
};

const defaultRoomItem = {
  is_room: true,
  room: "ROOM",
};

// Fetch scope data on component mount
onMounted(async () => {
  await fetchTradeOptions();
  await getScope();
  setTooltip();
  props.setActions({
    save: handleSave,
    reset: () => openResetModal("all"),
    resetAll: () => openResetModal("all"),
    resetCosts: () => openResetModal("cost"),
    print: handleDownloadScopePDF,
  });
  populateSubitems();
});

// Watch for changes in propertyId to refresh scope data
watch(
  () => props.propertyId,
  async (newVal) => {
    if (newVal) {
      await getScope();
    }
  },
);

const escapeHtml = (text) =>
  String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Keep the bullet allowlist narrow. Broad arrow-like glyphs caused some
// wrapped PDF lines to be misread as list items.
const BULLET_CHARS = "•●▪▫■□◦‣";
const bulletCharClass = `[${BULLET_CHARS}]`;
const bulletLineRegex = new RegExp(`^\\s*(?:[*]|${bulletCharClass})\\s+`);
const isOrderedLine = (line) => /^\s*(\d{1,3}[.)])\s*(?=[A-Z"“])/.test(line);
const orderedMarkerRegex = /(^|[\s\n])(\d{1,3}[.)])\s*(?=[A-Z"“])/g;
const bulletMarkerRegex = new RegExp(
  `(^|[\\s\\n])((?:[*]|${bulletCharClass}))\\s+`,
  "g",
);
const noteMarkerRegex = /\s+(Note\s+\d+\s*:)\s*/gi;
const paragraphBreakRegex = /^\s*Note\s+\d+\s*:/i;

const normalizeImportedText = (text) =>
  String(text || "")
    .replace(/\r/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/\t/g, " ")
    .replace(noteMarkerRegex, "\n$1 ")
    .replace(/[ ]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const splitByMarker = (text, regex) => {
  const matches = [];
  regex.lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const markerStart = match.index + match[1].length;
    const contentStart = match.index + match[0].length;
    matches.push({ markerStart, contentStart, marker: match[2] });
  }

  if (matches.length === 0) {
    return { intro: text.trim(), items: [] };
  }

  const intro = text.slice(0, matches[0].markerStart).trim();
  const items = matches.map((entry, index) => {
    const nextStart =
      index + 1 < matches.length ? matches[index + 1].markerStart : text.length;
    return text.slice(entry.contentStart, nextStart).trim();
  });

  return { intro, items };
};

const splitParagraphBlocks = (text) => {
  const normalized = normalizeImportedText(text);
  if (!normalized) {
    return [];
  }

  const paragraphs = [];
  let currentParts = [];

  const flushCurrent = () => {
    if (currentParts.length > 0) {
      paragraphs.push(currentParts.join(" ").trim());
      currentParts = [];
    }
  };

  normalized.split("\n").forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }

    if (paragraphBreakRegex.test(trimmed)) {
      flushCurrent();
      paragraphs.push(trimmed);
      return;
    }

    currentParts.push(trimmed);
  });

  flushCurrent();
  return paragraphs;
};

const renderParagraphs = (text) =>
  splitParagraphBlocks(text)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");

const hasStructuredListMarkers = (text) => {
  const normalized = normalizeImportedText(text);
  if (!normalized) return false;

  const orderedMatches = normalized.match(
    /(^|[\s\n])(\d{1,3}[.)])\s*(?=[A-Z"“])/g,
  );
  const bulletMatches = normalized.match(
    new RegExp(`(^|[\\s\\n])((?:[-*]|${bulletCharClass}))\\s+`, "g"),
  );

  return (
    (orderedMatches && orderedMatches.length > 0) ||
    (bulletMatches && bulletMatches.length > 1)
  );
};

const renderListItemContent = (text) => {
  const normalized = normalizeImportedText(text);
  const bullets = splitByMarker(normalized, bulletMarkerRegex);

  if (bullets.items.length > 0) {
    const parts = [];
    if (bullets.intro) {
      parts.push(renderParagraphs(bullets.intro));
    }
    parts.push(
      `<ul>${bullets.items
        .map((item) => `<li>${renderParagraphs(item) || escapeHtml(item)}</li>`)
        .join("")}</ul>`,
    );
    return parts.join("");
  }

  return renderParagraphs(normalized) || escapeHtml(normalized);
};

const renderTopLevelBullets = (text) => {
  const normalized = normalizeImportedText(text);
  const bullets = splitByMarker(normalized, bulletMarkerRegex);
  if (bullets.items.length === 0) {
    return "";
  }

  const parts = [];
  if (bullets.intro) {
    parts.push(renderParagraphs(bullets.intro));
  }

  const bulletItems = [...bullets.items];
  let trailingAfterList = "";
  if (bulletItems.length > 0) {
    const lastItem = bulletItems[bulletItems.length - 1];
    const trailingMatch = lastItem.match(
      /^(.*?)(\.\s+(?:Where|Refer|Note)\b.*)$/i,
    );
    if (trailingMatch) {
      bulletItems[bulletItems.length - 1] = trailingMatch[1].trim();
      trailingAfterList = trailingMatch[2].trim();
    }
  }

  const listHtml = bulletItems
    .filter((item) => item.trim())
    .map((item) => `<li>${renderParagraphs(item) || escapeHtml(item)}</li>`)
    .join("");

  if (listHtml) {
    parts.push(`<ul>${listHtml}</ul>`);
  }

  if (trailingAfterList) {
    parts.push(renderParagraphs(trailingAfterList.replace(/^\.\s*/, "")));
  }

  return parts.join("");
};

const plainTextToRichHtml = (text) => {
  const normalizedText = normalizeImportedText(text);
  if (!normalizedText) {
    return "";
  }

  const ordered = splitByMarker(normalizedText, orderedMarkerRegex);
  if (ordered.items.length > 0) {
    const introHtml = ordered.intro ? renderParagraphs(ordered.intro) : "";
    const listHtml = `<ol>${ordered.items
      .map((item) => `<li>${renderListItemContent(item)}</li>`)
      .join("")}</ol>`;
    return `${introHtml}${listHtml}`;
  }

  const topLevelBulletsHtml = renderTopLevelBullets(normalizedText);
  if (topLevelBulletsHtml) {
    return topLevelBulletsHtml;
  }

  const lines = normalizedText.split("\n");
  const htmlParts = [];
  let currentList = null;
  let proseLines = [];

  const closeList = () => {
    if (currentList) {
      htmlParts.push(`</${currentList}>`);
      currentList = null;
    }
  };

  const flushProse = () => {
    if (proseLines.length === 0) return;
    htmlParts.push(renderParagraphs(proseLines.join("\n")));
    proseLines = [];
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }

    if (bulletLineRegex.test(line)) {
      flushProse();
      if (currentList !== "ul") {
        closeList();
        currentList = "ul";
        htmlParts.push("<ul>");
      }
      htmlParts.push(
        `<li>${escapeHtml(trimmed.replace(bulletLineRegex, ""))}</li>`,
      );
      return;
    }

    if (isOrderedLine(line)) {
      flushProse();
      if (currentList !== "ol") {
        closeList();
        currentList = "ol";
        htmlParts.push("<ol>");
      }
      htmlParts.push(
        `<li>${escapeHtml(trimmed.replace(/^\s*(\d{1,3}[.)])\s*/, ""))}</li>`,
      );
      return;
    }

    closeList();
    proseLines.push(trimmed);
  });

  flushProse();
  closeList();
  return htmlParts.join("");
};

const richHtmlToPlainText = (html, fallback = "") => {
  if (!html) {
    return String(fallback || "");
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) {
    return String(fallback || "");
  }

  const lines = [];
  const pushLine = (value) => {
    if (value) {
      lines.push(value);
    }
  };

  Array.from(root.childNodes).forEach((child) => {
    if (child.nodeType !== Node.ELEMENT_NODE) {
      const text = child.textContent.replace(/\s+/g, " ").trim();
      pushLine(text);
      return;
    }

    const tag = child.tagName.toLowerCase();
    if (tag === "ul" || tag === "ol") {
      Array.from(child.children).forEach((li, index) => {
        const text = li.textContent.replace(/\s+/g, " ").trim();
        if (text) {
          pushLine(tag === "ol" ? `${index + 1}. ${text}` : `- ${text}`);
        }
      });
      return;
    }

    const text = child.textContent.replace(/\s+/g, " ").trim();
    pushLine(text);
  });

  return lines.join("\n").trim() || String(fallback || "");
};

const htmlHasRichFormatting = (html) => {
  const lowered = String(html || "").toLowerCase();
  return ["<ul", "<ol", "<strong", "<em", "<u"].some((tag) =>
    lowered.includes(tag),
  );
};

const normalizeItemDescription = (item) => {
  const sourceHtml =
    typeof item?.item_html === "string" ? item.item_html.trim() : "";
  const sourceText = typeof item?.item === "string" ? item.item : "";
  const htmlText = sourceHtml
    ? richHtmlToPlainText(sourceHtml, sourceText)
    : sourceText;
  const sourceHasRichFormatting = htmlHasRichFormatting(sourceHtml);
  const shouldRebuildHtml =
    !sourceHtml ||
    (!sourceHasRichFormatting && hasStructuredListMarkers(htmlText)) ||
    (!sourceHasRichFormatting && Boolean(sourceText.trim()));
  const candidateSource = sourceText.trim() ? sourceText : htmlText;
  const itemHtml = shouldRebuildHtml
    ? plainTextToRichHtml(candidateSource)
    : sourceHtml;
  const itemText = richHtmlToPlainText(itemHtml, sourceText);

  return {
    item: itemText,
    item_html: itemHtml,
  };
};

const fetchTradeOptions = async () => {
  try {
    const response = await axios.get(`${apiEndpoint}/trades/`, headers);
    scopeTradeOptions.value = (response.data || []).map((trade) => {
      const code = (trade.code || "").trim();
      return {
        value: code,
        label: code,
      };
    });
  } catch (error) {
    toast.error("Failed to retrieve trades list");
    console.log("Error:", error);
    scopeTradeOptions.value = [];
  }
};

/**
 * Fetch the scope data for the property
 */
const getScope = async () => {
  loading.value = true;
  try {
    const response = await axios.get(
      `${apiEndpoint}/property/get-scope/${props.propertyId}`,
      headers,
    );
    if (Object.keys(response.data.scope).length !== 0) {
      // Assign scope
      const cleanedScope = {};
      Object.entries(response.data.scope).forEach(([sectionId, section]) => {
        cleanedScope[sectionId] = {
          ...section,
          items: section.items.map((item) => normalizeItem(item)),
        };
      });

      // Assign cleaned scope
      scopeObj.value = cleanedScope;

      // Recalculate totals for all sections after loading
      Object.keys(scopeObj.value).forEach((sectionId) => {
        calculateSectionTotal(sectionId);
      });
    } else {
      // If no scope found then set to default
      scopeObj.value = normalizeScope(scopeItemsDefault);
      Object.keys(scopeObj.value).forEach((sectionId) =>
        calculateSectionTotal(sectionId),
      );
    }
  } catch (error) {
    toast.error("Failed to retrieve scope data");
    console.log("Error:", error);
  } finally {
    loading.value = false;
  }
};

/**
 * Save the current scope data
 */
const handleSave = async () => {
  loading.value = true;
  try {
    scopeObj.value = normalizeScope(scopeObj.value);
    await axios.put(
      `${apiEndpoint}/property/save-scope/${props.propertyId}/`,
      {
        scope: scopeObj.value,
      },
      headers,
    );
    toast.success("Property scope saved successfully");
    unsavedChanges.value = false;
    await getScope();
    await props.loadContract();
  } catch (error) {
    console.error(error);
    toast.error("An error occurred while saving property scope");
  } finally {
    loading.value = false;
  }
};

/**
 * Add a new item to a section
 * @param {String} sectionId - The ID of the section
 */
const handleAddItem = (sectionId, scopeIndex = null, addRoom = false) => {
  // Define the new item
  const itemToAdd = addRoom ? defaultRoomItem : defaultItem;
  if (scopeObj.value[sectionId]) {
    const items = scopeObj.value[sectionId].items;
    if (scopeIndex === null) {
      items.push({ ...itemToAdd });
    } else {
      items.splice(scopeIndex + 1, 0, { ...itemToAdd });
      // Re-order items after the new item
    }
  } else {
    scopeObj.value[sectionId] = {
      items: [{ ...itemToAdd }],
    };
  }
  unsavedChanges.value = true;
};

/**
 * Handle changes to a cell in the scope table
 * @param {String} sectionId - The ID of the section
 * @param {Number} itemIndex - The index of the item
 * @param {String} key - The key of the item property
 * @param {String|Number} value - The new value for the item property
 */
const handleCellChange = (sectionId, itemIndex, key, value) => {
  // Special handling for trade_code which is now an array
  if (key === "trade_code") {
    const trades = Array.isArray(value) ? value : [value];
    scopeObj.value[sectionId].items[itemIndex][key] = trades;

    // prune and init trade_costs to match trades
    const currentCosts =
      scopeObj.value[sectionId].items[itemIndex].trade_costs || {};
    const prunedCosts = Object.fromEntries(
      Object.entries(currentCosts).filter(([t]) => trades.includes(t)),
    );
    trades.forEach((t) => {
      if (!(t in prunedCosts)) prunedCosts[t] = 0;
    });
    scopeObj.value[sectionId].items[itemIndex].trade_costs = prunedCosts;
  } else if (key === "trade_costs") {
    scopeObj.value[sectionId].items[itemIndex].trade_costs = {
      ...(scopeObj.value[sectionId].items[itemIndex].trade_costs || {}),
      ...value,
    };
  } else {
    scopeObj.value[sectionId].items[itemIndex][key] =
      key === "cost" ? Number(value).toFixed(2) : value;
  }

  // Sync legacy cost with current trade_costs sum
  const normalized = normalizeItem(scopeObj.value[sectionId].items[itemIndex]);
  scopeObj.value[sectionId].items[itemIndex] = normalized;

  // If the changed cell impacts totals, recalc section total
  if (["cost", "subitem", "trade_costs", "trade_code"].includes(key)) {
    calculateSectionTotal(sectionId);
  }
  unsavedChanges.value = true;
};

/**
 * Delete an item from a section
 * @param {String} sectionId - The ID of the section
 * @param {Number} itemIndex - The index of the item
 */
const handleDeleteItem = (sectionId, itemIndex) => {
  scopeObj.value[sectionId].items.splice(itemIndex, 1);
  calculateSectionTotal(sectionId);
  unsavedChanges.value = true;
};

/**
 * Calculate the total cost for a section
 * @param {String} sectionId - The ID of the section
 */
const sumItemCost = (item) => {
  if (item && item.trade_costs && typeof item.trade_costs === "object") {
    const total = Object.values(item.trade_costs).reduce(
      (acc, val) => acc + (Number(val) || 0),
      0,
    );
    return Number(total.toFixed(2));
  }
  return Number(item?.cost || 0);
};

const normalizeItem = (item) => {
  // ensure structure and sync legacy cost
  const tradesRaw = item.trade_code || [];
  const tradesArray = Array.isArray(tradesRaw) ? tradesRaw : [tradesRaw];
  const normalizedTrades = Array.from(
    new Set(
      tradesArray.flatMap((t) =>
        typeof t === "string"
          ? t
              .split(/[,&/]/)
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      ),
    ),
  );
  const tradeCosts =
    item.trade_costs && typeof item.trade_costs === "object"
      ? { ...item.trade_costs }
      : {};
  const costTotal = sumItemCost({ ...item, trade_costs: tradeCosts });
  const description = normalizeItemDescription(item);
  return {
    ...item,
    ...description,
    trade_code: normalizedTrades,
    trade_costs: tradeCosts,
    cost: Number(costTotal).toFixed(2),
  };
};

const normalizeScope = (scope) => {
  const out = {};
  Object.entries(scope || {}).forEach(([sectionId, section]) => {
    out[sectionId] = {
      ...section,
      items: (section.items || []).map((itm) => normalizeItem(itm)),
    };
  });
  return out;
};

const calculateSectionTotal = (sectionId) => {
  if (!scopeObj.value[sectionId] || !scopeObj.value[sectionId].items) {
    scopeObj.value[sectionId] = {
      items: [],
      total: 0.0,
      sectionSubtotal: 0.0,
    };
    return;
  } else if (!scopeObj.value[sectionId].total) {
    scopeObj.value[sectionId].total = 0.0;
  } else if (!scopeObj.value[sectionId].sectionSubtotal) {
    scopeObj.value[sectionId].sectionSubtotal = 0.0;
  }

  // Calculate total by multiplying qty and cost for each item and summing
  const total = scopeObj.value[sectionId].items.reduce((acc, item) => {
    const cost = sumItemCost(item);
    return acc + cost;
  }, 0);

  // Calculate section subtotal which is the addition of the cost of all items that do not have a 'subitem' value in them
  const sectionSubtotal = scopeObj.value[sectionId].items
    .filter((item) => !item.subitem)
    .reduce((acc, item) => {
      const cost = sumItemCost(item);
      return acc + cost;
    }, 0);

  // Update the total and section subtotal for this section
  scopeObj.value[sectionId].total = Number(total.toFixed(2));
  scopeObj.value[sectionId].sectionSubtotal = Number(
    sectionSubtotal.toFixed(2),
  );
};

/**
 * Reset helpers and modal
 */
const openResetModal = (type) => {
  if (disabledEdit) return;
  resetModal.value = { open: true, type };
};

const closeResetModal = () => {
  resetModal.value = { open: false, type: null };
};

const handleResetAll = () => {
  // Deep clone to avoid mutating the default template
  scopeObj.value = normalizeScope(
    JSON.parse(JSON.stringify(scopeItemsDefault)),
  );
  Object.keys(scopeObj.value || {}).forEach((sectionId) => {
    calculateSectionTotal(sectionId);
  });
  unsavedChanges.value = true;
};

const handleResetCostsOnly = () => {
  Object.keys(scopeObj.value || {}).forEach((sectionId) => {
    const section = scopeObj.value[sectionId];
    if (!section || !Array.isArray(section.items)) return;
    section.items.forEach((item) => {
      if (!item.is_room) {
        item.trade_costs = {};
        item.cost = "0.00";
      }
    });
    calculateSectionTotal(sectionId);
  });
  unsavedChanges.value = true;
};

const confirmReset = () => {
  if (resetModal.value.type === "all") {
    handleResetAll();
  } else if (resetModal.value.type === "cost") {
    handleResetCostsOnly();
  }
  closeResetModal();
};

/**
 * Download the scope as a PDF
 */
const handleDownloadScopePDF = async () => {
  loading.value = true;
  try {
    const response = await axios.get(
      `${store.state.apiEndpoint}/property/download_scope/${props.propertyId}/`,
      {
        ...headers,
        responseType: "arraybuffer",
      },
    );

    // Create a Blob from the response data
    const blob = new Blob([response.data], { type: "application/pdf" });

    // Create an Object URL for the Blob
    const url = window.URL.createObjectURL(blob);

    // Create a link element and trigger the download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `scope_${props.address}.pdf`);
    document.body.appendChild(link);
    link.click();

    // Clean up the Object URL
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
    toast.error("An error occurred while downloading Schedule 2");
  } finally {
    loading.value = false;
  }
};

// Populate subitems for sections 2 and 3
const populateSubitems = () => {
  // Iterate through scope sections and add items to section2Subitems
  scopeSections.forEach((section) => {
    if (!section.items) return;
    section.items.forEach((item) => {
      section2Subitems.value.push(item);
    });
  });
};

const removeTag = (sectionId, itemIndex, tagToRemove) => {
  const currentTags = scopeObj.value[sectionId].items[itemIndex].trade_code;
  const updatedTags = currentTags.filter((tag) => tag !== tagToRemove);
  handleCellChange(sectionId, itemIndex, "trade_code", updatedTags);
};

const formatTradeCodeLabel = (tradeCode) => {
  const value = String(tradeCode || "").trim();
  if (value.length <= 5) {
    return value;
  }
  return `${value.slice(0, 5)}...`;
};

const handleDescriptionChange = (sectionId, itemIndex, { html, text }) => {
  const targetItem = scopeObj.value[sectionId]?.items?.[itemIndex];
  if (!targetItem) return;

  targetItem.item_html = html;
  targetItem.item = richHtmlToPlainText(html, text);
  unsavedChanges.value = true;
};

const getNavigableCostInputs = () =>
  Array.from(document.querySelectorAll(".scope-cost-input")).filter(
    (el) => !el.disabled && el.offsetParent !== null,
  );

const focusAndSelectInput = (input) => {
  if (!input) return;
  input.focus();
  input.select();
};

/**
 * Navigate to the next or previous cost input across all sections.
 * @param {HTMLInputElement} currentInput - Current focused cost input.
 * @param {number} step - 1 for next/down, -1 for previous/up.
 */
const moveCostFocus = (currentInput, step) => {
  const allInputs = getNavigableCostInputs();
  const currentIndex = allInputs.indexOf(currentInput);
  if (currentIndex === -1) return;

  const targetInput = allInputs[currentIndex + step];
  if (!targetInput) return;

  focusAndSelectInput(targetInput);
};

/**
 * Handle keydown events for cost input navigation
 * @param {KeyboardEvent} event - The keyboard event
 */
const handleCostKeydown = (event) => {
  switch (event.key) {
    case "Enter":
      event.preventDefault();
      moveCostFocus(event.target, 1);
      break;
    case "ArrowDown":
      event.preventDefault();
      moveCostFocus(event.target, 1);
      break;
    case "ArrowUp":
      event.preventDefault();
      moveCostFocus(event.target, -1);
      break;
  }
};
</script>

<template>
  <LoadingSpinner v-if="loading" message="Loading..." :fullScreen="true" />

  <div>
    <div class="scope-header d-flex justify-content-between">
      <div>
        <div class="scope-header-div">
          <h1 class="scope-h1 col-md-12">
            Capital Improvement Scope of Work: Dwelling
          </h1>
        </div>
        <h5 class="fwt-bold scope-h5">Work Detail by Section: GST Exclusive</h5>
      </div>
    </div>
    <div class="property-scope">
      <div class="row col-md-12">
        <p class="p-0 mb-0 fwt-bold">Works Contract ID:</p>
        <span class="gray-cell col-6 ms-0 ps-0">{{ contractId }}</span>
        <div class="pt-3"></div>
        <p class="p-0 mb-0 fwt-bold">Address:</p>
        <span class="gray-cell col-6 ms-0 ps-0">{{ address }}</span>
      </div>
    </div>
    <div class="scope-table my-3 row">
      <div
        v-for="(scopeSect, scopeSectIdx) in scopeSections"
        :key="scopeSect.id"
      >
        <div class="scope-section-row row col-12 mt-3">
          <div
            class="scope-section-code border border-dark border-1 col-1 text-center fwt-bold"
          >
            {{ scopeSect.code }}
          </div>

          <div
            class="scope-section-code border border-dark border-1 col-5 fwt-bold gray-cell p-0"
          >
            {{ scopeSect.title }}
          </div>
          <!-- <div class="col-1 p-0 ms-2">
            <button
              v-if="propertyMetadata?.sections?.[scopeSect.id]?.page"
              data-bs-toggle="tooltip"
              title="Click to view section in the Scope Document"
              class="btn btn-sm btn-link p-0 m-0"
              @click="setSection(scopeSect.id)"
            >
              <i class="fas fa-bookmark"></i>
            </button>
          </div> -->
        </div>
        <div
          class="scope-section-row row col-12 mt-3 d-flex flex-nowrap align-items-center"
        >
          <ScopeItemRow
            v-for="(colSize, index) in ['1', '2', '6', '1', '1', '2']"
            :key="index"
            :colSize="colSize"
            :textCenter="index === 0"
            :class="{
              'scope-description-column': index === 2,
              'scope-price-column': index === 5,
            }"
          >
            <span v-if="index === 0">Code</span>
            <span v-else-if="index === 1">Trade</span>
            <span v-else-if="index === 2">Description &/or Location:</span>
            <span v-else-if="index === 3">Unit</span>
            <span v-else-if="index === 4">Qty</span>
            <span v-else>Builders Price</span>
          </ScopeItemRow>
          <div class="col-2 fwt-bold">
            <span>Related EESHP </span>
          </div>
        </div>
        <div
          v-for="(scopeItem, scopeItemIndex) in scopeObj[scopeSect.id]?.items"
          :key="scopeItem.id"
        >
          <div class="d-flex gap-1 align-items-center">
            <div
              v-if="scopeItem && scopeItem.is_room"
              class="scope-section-row row col-12"
            >
              <div
                class="border border-dark border-1 fwt-bold p-0 d-flex align-items-center gray-cell mt-2"
                style="width: 15%"
              >
                <input
                  type="text"
                  class="form-control"
                  :value="scopeItem.room"
                  :disabled="disabledEdit"
                  style="background-color: #e9ecef"
                  @input="
                    (event) =>
                      handleCellChange(
                        scopeSect.id,
                        scopeItemIndex,
                        'room',
                        event.target.value,
                      )
                  "
                />
              </div>
            </div>
            <div
              v-if="scopeItem && !scopeItem.is_room"
              :class="
                scopeItem.item_code?.toLowerCase() === 'eep' ? 'is-eep' : ''
              "
              class="scope-section-row row col-12 flex-nowrap"
            >
              <ScopeItemRow colSize="1" :textCenter="true">
                <select
                  class="form-control"
                  :id="`item-code-${scopeItemIndex}-code-for${scopeItem.id}`"
                  :value="scopeItem.item_code"
                  :disabled="disabledEdit"
                  @change="
                    (event) =>
                      handleCellChange(
                        scopeSect.id,
                        scopeItemIndex,
                        'item_code',
                        event.target.value,
                      )
                  "
                >
                  <option
                    v-for="unit in scopeCodeOptions"
                    :value="unit.value"
                    :key="unit.value"
                  >
                    {{ unit.label }}
                  </option>
                </select>
              </ScopeItemRow>
              <ScopeItemRow
                colSize="2"
                :textCenter="true"
                :class="scopeItem.trade_code?.length > 0 ? 'has-trade' : ''"
              >
                <Multiselect
                  v-model="scopeItem.trade_code"
                  :options="scopeTradeOptions"
                  :disabled="disabledEdit"
                  :height="200"
                  mode="tags"
                  :closeOnSelect="true"
                  placeholder="BLANK"
                  @change="
                    (value) =>
                      handleCellChange(
                        scopeSect.id,
                        scopeItemIndex,
                        'trade_code',
                        value,
                      )
                  "
                >
                  <template #tag="{ option }">
                    <div
                      class="multiselect-tag"
                      :title="option.label || option"
                    >
                      {{ formatTradeCodeLabel(option.label || option) }}
                      <span
                        v-if="!disabledEdit"
                        class="multiselect-tag-remove"
                        @mousedown.prevent
                        @click.stop="
                          removeTag(
                            scopeSect.id,
                            scopeItemIndex,
                            option.value || option,
                          )
                        "
                      >
                        ✕
                      </span>
                    </div>
                  </template>
                </Multiselect>
              </ScopeItemRow>
              <ScopeItemRow
                colSize="6"
                class="scope-description-cell scope-description-column"
              >
                <ScopeRichTextEditor
                  :modelValue="scopeItem.item_html"
                  :disabled="disabledEdit"
                  @change="
                    (payload) =>
                      handleDescriptionChange(
                        scopeSect.id,
                        scopeItemIndex,
                        payload,
                      )
                  "
                />
              </ScopeItemRow>
              <ScopeItemRow colSize="1">
                <select
                  class="form-control"
                  :id="`unit-${scopeItemIndex}-for${scopeItem.id}`"
                  :value="scopeItem.unit?.toLowerCase()"
                  :disabled="disabledEdit"
                  @change="
                    (event) =>
                      handleCellChange(
                        scopeSect.id,
                        scopeItemIndex,
                        'unit',
                        event.target.value,
                      )
                  "
                >
                  <option
                    v-for="unit in scopeItemUnitTypes"
                    :value="unit.value"
                    :key="unit.value"
                  >
                    {{ unit.label }}
                  </option>
                </select>
              </ScopeItemRow>
              <ScopeItemRow colSize="1">
                <input
                  type="number"
                  :value="scopeItem.qty"
                  class="form-control"
                  :id="`qty-${scopeItemIndex}-for${scopeItem.id}`"
                  :disabled="disabledEdit"
                  @blur="
                    (event) =>
                      handleCellChange(
                        scopeSect.id,
                        scopeItemIndex,
                        'qty',
                        event.target.value,
                      )
                  "
                />
              </ScopeItemRow>
              <ScopeItemRow colSize="2" class="scope-price-column">
                <div class="d-flex flex-wrap gap-2 align-items-center">
                  <div
                    v-for="trade in scopeItem.trade_code || []"
                    :key="`${scopeItemIndex}-${trade}`"
                    class="input-group trade-cost-input"
                  >
                    <span class="input-group-text bg-light" :title="trade">
                      {{ formatTradeCodeLabel(trade) }}
                    </span>
                    <input
                      class="form-control ps-2 scope-cost-input"
                      type="number"
                      placeholder="$0.00"
                      :value="scopeItem.trade_costs?.[trade]"
                      :id="`cost-${scopeItemIndex}-${trade}-for-${scopeSectIdx}`"
                      :disabled="disabledEdit"
                      @change="
                        (event) =>
                          handleCellChange(
                            scopeSect.id,
                            scopeItemIndex,
                            'trade_costs',
                            { [trade]: Number(event.target.value).toFixed(2) },
                          )
                      "
                      @keydown="handleCostKeydown"
                      @focus="(event) => focusAndSelectInput(event.target)"
                      step="0.01"
                    />
                  </div>
                  <div
                    v-if="
                      !scopeItem.trade_code || scopeItem.trade_code.length === 0
                    "
                    class="text-muted small"
                  >
                    Select a trade to enter costs.
                  </div>
                </div>
              </ScopeItemRow>
              <div class="col-2 related-eeshp-cell">
                <div
                  v-if="scopeItem.item_code === 'EEP'"
                  class="related-eeshp-select"
                >
                  <select
                    class="form-control"
                    :id="`schedule2-subitem-${scopeItemIndex}-for${scopeItem.id}`"
                    :disabled="disabledEdit"
                    @blur="
                      (event) =>
                        handleCellChange(
                          scopeSect.id,
                          scopeItemIndex,
                          'subitem',
                          event.target.value,
                        )
                    "
                    :value="scopeItem.subitem"
                  >
                    <option value="">N/A</option>
                    <option
                      v-for="item in section2Subitems"
                      :value="item.id"
                      :key="item.id"
                    >
                      {{ item.title }}
                      {{ item.code == "EESHP" ? "(EESHP)" : "" }}
                    </option>
                  </select>
                </div>
                <div
                  v-if="!disabledEdit"
                  class="related-eeshp-actions d-flex justify-content-end"
                >
                  <button
                    class="btn btn-link text-danger p-0 m-0"
                    @click="handleDeleteItem(scopeSect.id, scopeItemIndex)"
                  >
                    <i class="fas fa-trash fs-5"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            class="scope-section-row row col-10 mt-3 p-0"
            v-if="scopeObj[scopeSect.id]?.items[scopeItemIndex + 1]?.is_room"
          >
            <div class="d-flex justify-content-start" v-if="!disabledEdit">
              <button
                class="btn btn-primary"
                @click="handleAddItem(scopeSect.id, scopeItemIndex)"
              >
                <i class="fas fa-plus"></i> Add Item
              </button>
              <button
                class="btn btn-primary ms-3"
                @click="handleAddItem(scopeSect.id, scopeItemIndex, true)"
              >
                <i class="fas fa-plus"></i> Add Room
              </button>
            </div>
          </div>
        </div>

        <div
          class="scope-section-row row col-12 d-flex flex-nowrap"
          :id="`total-for${scopeSect.id}`"
        >
          <div class="col-1 scope-total-empty"></div>
          <div class="col-2 scope-total-empty"></div>
          <div class="col-6 scope-total-empty scope-description-column"></div>
          <div class="col-1 scope-total-empty"></div>
          <div class="col-1 scope-total-empty"></div>
          <div
            class="col-2 scope-total-value scope-price-column border border-dark border-1 gray-cell text-end"
          >
            {{ toCurrency(scopeObj[scopeSect.id]?.total) }}
          </div>
          <div class="col-2 scope-total-related-spacer"></div>
        </div>
        <div class="scope-section-row row col-10 mt-0">
          <div class="d-flex justify-content-start" v-if="!disabledEdit">
            <button
              class="btn btn-primary"
              @click="handleAddItem(scopeSect.id, null)"
            >
              <i class="fas fa-plus"></i> Add Item
            </button>
            <button
              class="btn btn-primary ms-3"
              @click="handleAddItem(scopeSect.id, null, true)"
            >
              <i class="fas fa-plus"></i> Add Room
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="scopeFileUrl && showPdf" class="pdf-viewer pdf-fixed-bottom">
    <PDFVuer
      :pdfUrl="scopeFileUrl"
      :pageNumber="currentSection?.page"
      :hidePdf="() => (showPdf = false)"
      :highlightText="currentSection?.schedule2_title"
    />
  </div>

  <!-- Reset confirmation modal -->
  <div v-if="resetModal.open">
    <div class="modal-backdrop fade show"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{
                resetModal.type === "all"
                  ? "Reset all scope data?"
                  : "Reset all costs to $0.00?"
              }}
            </h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="closeResetModal"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-2">
              Resetting will <strong>not</strong> save automatically. Click Save
              after resetting to keep the change, or refresh the page to restore
              current data.
            </p>
            <p v-if="resetModal.type === 'all'" class="mb-0">
              This clears the entire scope. Please proceed with caution.
            </p>
            <p v-else class="mb-0">
              This sets every item cost to $0.00 but keeps descriptions, trades,
              units, and quantities.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-link" @click="closeResetModal">
              Cancel
            </button>
            <button
              type="button"
              class="btn"
              :class="resetModal.type === 'all' ? 'btn-danger' : 'btn-warning'"
              @click="confirmReset"
            >
              {{ resetModal.type === "all" ? "Reset all" : "Reset costs" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
@import "@vueform/multiselect/themes/default.css";

div,
input,
select,
textarea,
option {
  font-size: 12px;
}

.scope-description-cell {
  align-items: stretch !important;
}

.scope-description-column {
  flex: 0 0 45.833333%;
  max-width: 45.833333%;
}

.scope-price-column {
  flex: 0 0 20.833333%;
  max-width: 20.833333%;
}

.scope-total-value {
  flex: 0 0 20.833333%;
  max-width: 20.833333%;
}

.scope-total-empty,
.scope-total-related-spacer {
  min-height: 1px;
}

.related-eeshp-cell {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  padding-left: 10px;
}

.related-eeshp-select {
  padding-top: 2px;
}

.related-eeshp-actions {
  min-height: 20px;
  width: 100%;
  padding-right: 4px;
  margin-left: auto;
}

:deep(.form-control),
:deep(.form-select) {
  font-size: 12px;
  padding: 0.35rem 0.55rem;
}

:deep(.input-group-text) {
  font-size: 12px;
  padding: 0.35rem 0.55rem;
}

:deep(.scope-rich-text-editor .ql-container) {
  border-color: #d2d6da;
  background: #fff !important;
  font-size: 12px !important;
}

:deep(.scope-rich-text-editor .ql-editor) {
  font-family: inherit;
  background: #fff !important;
  font-size: 12px !important;
  padding: 0.4rem 0.6rem !important;
}

:deep(.scope-rich-text-editor .ql-editor *),
:deep(.scope-rich-text-editor .ql-editor p),
:deep(.scope-rich-text-editor .ql-editor ol),
:deep(.scope-rich-text-editor .ql-editor ul),
:deep(.scope-rich-text-editor .ql-editor li),
:deep(.scope-rich-text-editor .ql-editor strong),
:deep(.scope-rich-text-editor .ql-editor em),
:deep(.scope-rich-text-editor .ql-editor u) {
  font-size: 12px !important;
}

/* Updated multiselect styles */
:deep(.multiselect) {
  min-height: 31px;
  font-size: 12px;
}

:deep(.multiselect-tag) {
  background: #00047b;
  color: white;
  font-size: 12px;
  padding: 2px 6px;
  margin: 2px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.multiselect-tag-remove) {
  color: white;
}

:deep(.multiselect-input) {
  font-size: 12px;
}

:deep(.multiselect-dropdown) {
  max-height: 300px !important;
}

:deep(.multiselect-option) {
  font-size: 12px !important;
  padding: 0.35rem 0.55rem !important;
}

:deep(.multiselect-options) {
  font-size: 12px;
}

:deep(.multiselect-multiple-label) {
  padding: 3px 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  font-size: 12px;
}

:deep(.multiselect-placeholder) {
  padding: 3px 4px;
  font-size: 12px;
}

/* Make the dropdown menu appear above other elements */
:deep(.multiselect-dropdown) {
  z-index: 100;
}

/* Style for the selected options in the box */
:deep(.multiselect-tags) {
  margin: 0;
  padding: 1px 2px;
}

/* Adjust the input wrapper to show full content */
:deep(.multiselect-wrapper) {
  min-height: 31px;
  display: flex;
  align-items: center;
}

:deep(.multiselect-tag) {
  background: #00047b;
  color: white;
  font-size: 12px;
  padding: 1px 5px;
  margin: 1px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

:deep(.multiselect-tag-remove) {
  color: white;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

:deep(.multiselect-tag-remove:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}
</style>
